import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Calendar, X } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Convert Google Drive URLs to reliable thumbnail URLs
const convertGoogleDriveUrl = (url) => {
  if (!url) return url;
  if (url.includes('drive.google.com/thumbnail') || url.includes('lh3.googleusercontent.com')) return url;
  
  const fileMatch = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (fileMatch) return `https://drive.google.com/thumbnail?id=${fileMatch[1]}&sz=w800`;
  
  const openMatch = url.match(/drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/);
  if (openMatch) return `https://drive.google.com/thumbnail?id=${openMatch[1]}&sz=w800`;
  
  const ucMatch = url.match(/drive\.google\.com\/uc\?(?:export=view&)?id=([a-zA-Z0-9_-]+)/);
  if (ucMatch) return `https://drive.google.com/thumbnail?id=${ucMatch[1]}&sz=w800`;
  
  return url;
};

export const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  // Search on mount and when query param changes
  useEffect(() => {
    const urlQuery = searchParams.get('q');
    if (urlQuery && urlQuery.trim().length >= 2) {
      setQuery(urlQuery);
      performSearch(urlQuery);
    }
  }, [searchParams]);

  const performSearch = async (searchQuery) => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      setResults([]);
      setSearched(false);
      return;
    }

    setLoading(true);
    setSearched(true);

    try {
      const response = await fetch(`${API_URL}/api/search?q=${encodeURIComponent(searchQuery)}&limit=30`);
      if (response.ok) {
        const data = await response.json();
        setResults(data);
      } else {
        setResults([]);
      }
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim().length >= 2) {
      setSearchParams({ q: query });
      performSearch(query);
    }
  };

  const clearSearch = () => {
    setQuery('');
    setResults([]);
    setSearched(false);
    setSearchParams({});
  };

  return (
    <div className="min-h-screen" data-testid="search-page">
      <Helmet>
        <title>Search | Cultural Revolution</title>
        <meta name="description" content="Search articles, essays, and updates on Cultural Revolution." />
      </Helmet>

      {/* Search Header */}
      <section className="editorial-container py-12 lg:py-16 border-b border-border">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl editorial-serif mb-8 text-center">
          Search
        </h1>

        <form onSubmit={handleSubmit} className="max-w-2xl mx-auto">
          <div className="relative">
            <Search 
              className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" 
              size={20} 
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search articles, essays, updates..."
              className="w-full pl-12 pr-12 py-4 bg-background border border-border rounded-none text-base focus:outline-none focus:border-foreground transition-colors"
              data-testid="search-input"
              autoFocus
            />
            {query && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
              >
                <X size={20} />
              </button>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-2 text-center">
            Enter at least 2 characters to search
          </p>
        </form>
      </section>

      {/* Results */}
      <section className="editorial-container py-12 lg:py-16">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
          </div>
        ) : searched && results.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-muted-foreground text-sm uppercase tracking-widest mb-4">
              No results found
            </p>
            <p className="text-muted-foreground text-sm">
              Try different keywords or browse our content below
            </p>
          </div>
        ) : results.length > 0 ? (
          <>
            <p className="text-xs text-muted-foreground uppercase tracking-widest mb-8">
              {results.length} result{results.length !== 1 ? 's' : ''} for &ldquo;{query}&rdquo;
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-16">
              {results.map((item) => (
                <Link 
                  key={item.content_id} 
                  to={`/${item.slug}`}
                  className="block group"
                  data-testid={`search-result-${item.slug}`}
                >
                  {/* Image */}
                  {item.image && (
                    <div className="relative aspect-[4/3] mb-6 overflow-hidden bg-black">
                      <img
                        src={convertGoogleDriveUrl(item.image)}
                        alt={item.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-103 opacity-90"
                      />
                    </div>
                  )}

                  {/* Content */}
                  <div className="space-y-3">
                    {/* Type & Source */}
                    <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-widest">
                      <span className="px-2 py-0.5 bg-primary/10 text-primary">{item.type}</span>
                      {item.source && (
                        <>
                          <span>·</span>
                          <span>{item.source}</span>
                        </>
                      )}
                    </div>
                    
                    {/* Headline */}
                    <h3 className="text-xl lg:text-2xl editorial-serif leading-tight group-hover:accent-red transition-colors">
                      {item.title}
                    </h3>
                    
                    {/* Dek */}
                    <p className="text-sm text-foreground/70 leading-relaxed line-clamp-2">
                      {item.dek}
                    </p>
                    
                    {/* Date */}
                    <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-widest">
                      <span className="flex items-center gap-1">
                        <Calendar size={10} />
                        {new Date(item.date).toLocaleDateString('en-US', { 
                          month: 'short', 
                          year: 'numeric' 
                        })}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </>
        ) : null}
      </section>
    </div>
  );
};

export default SearchPage;
